import React from 'react';

import { getFormattedPrice } from '@tools/utils/common.tsx';
import type { PosPaymentMethod } from '../../api/usePosPaymentMethods';

export interface PosTopUpSectionProps {
  availableCustomerCredit: number;
  currency: string;
  topUpDirection: 'credit' | 'debit';
  topUpAmountDraft: string;
  topUpMethodId: number | null;
  topUpNote: string;
  topUpAmountError: string | null;
  paymentMethodOptions: PosPaymentMethod[];
  isTopUpActionDisabled: boolean;
  isProcessingTopUp: boolean;
  topUpButtonLabel: string;
  onDirectionChange: (direction: 'credit' | 'debit') => void;
  onAmountChange: (value: string) => void;
  onMethodChange: (methodId: number | null) => void;
  onNoteChange: (value: string) => void;
  onSubmit: () => void;
}

export const PosTopUpSection: React.FC<PosTopUpSectionProps> = ({
  availableCustomerCredit,
  currency,
  topUpDirection,
  topUpAmountDraft,
  topUpMethodId,
  topUpNote,
  topUpAmountError,
  paymentMethodOptions,
  isTopUpActionDisabled,
  isProcessingTopUp,
  topUpButtonLabel,
  onDirectionChange,
  onAmountChange,
  onMethodChange,
  onNoteChange,
  onSubmit,
}) => (
  <section className="pos-panel pos-topup" aria-label="Cargar o devolver saldo">
    <header className="pos-panel__header">
      <h2 className="pos-panel__title">Saldo del cliente</h2>
      <span className="pos-panel__meta">Disponible: {getFormattedPrice(availableCustomerCredit, currency)}</span>
    </header>
    <div className="pos-topup__body">
      <div className="pos-topup__direction" role="radiogroup" aria-label="Tipo de movimiento">
        <label className={`pos-topup__radio${topUpDirection === 'credit' ? ' is-active' : ''}`}>
          <input type="radio" name="topup-direction" value="credit" checked={topUpDirection === 'credit'} onChange={() => onDirectionChange('credit')} />
          Sumar saldo
        </label>
        <label className={`pos-topup__radio${topUpDirection === 'debit' ? ' is-active' : ''}`}>
          <input type="radio" name="topup-direction" value="debit" checked={topUpDirection === 'debit'} onChange={() => onDirectionChange('debit')} />
          Devolver saldo
        </label>
      </div>
      <label className="pos-topup__field">
        <span>Importe {topUpDirection === 'credit' ? 'a cargar' : 'a devolver'}</span>
        <input type="number" inputMode="decimal" min={0} step="0.01" value={topUpAmountDraft} onChange={(event) => onAmountChange(event.target.value)} placeholder="0.00" />
      </label>
      {topUpAmountError ? <p className="pos-topup__error" role="alert">{topUpAmountError}</p> : null}
      <label className="pos-topup__field">
        <span>Método de pago</span>
        <select value={topUpMethodId ?? 'none'} onChange={(event) => onMethodChange(event.target.value === 'none' ? null : Number(event.target.value))}>
          <option value="none">Seleccionar…</option>
          {paymentMethodOptions.map((method) => <option key={method.id} value={method.id}>{method.name}</option>)}
        </select>
      </label>
      <label className="pos-topup__field">
        <span>Notas (opcional)</span>
        <textarea rows={2} value={topUpNote} onChange={(event) => onNoteChange(event.target.value)} />
      </label>
      {topUpDirection === 'debit' ? <p className="pos-topup__hint">Disponible para devolver: {getFormattedPrice(availableCustomerCredit, currency)}</p> : null}
      <button type="button" className="pos-topup__submit" onClick={onSubmit} disabled={isTopUpActionDisabled}>
        {isProcessingTopUp ? 'Procesando…' : topUpButtonLabel}
      </button>
    </div>
  </section>
);

export default PosTopUpSection;
